import {
  addDays,
  formatDistanceToNowStrict,
  intlFormat,
  isBefore,
  isSameDay,
  Locale,
} from 'date-fns';
import {
  bg,
  da,
  de,
  enGB,
  es,
  et,
  fi,
  fr,
  hr,
  hu,
  it,
  lt,
  lv,
  nl,
  pl,
  pt,
  ro,
  sv,
} from 'date-fns/locale';

import { getSafeLanguage } from '@rebound/shared-models/utils';

import i18n from '@services/i18n.service';

export const ReturnStatusCodeTexts: Record<string, string> = {
  10: 'trackingStatus.status_10',
  20: 'trackingStatus.status_20',
  30: 'trackingStatus.status_30',
  40: 'trackingStatus.status_40',
  43: 'trackingStatus.status_43',
  45: 'trackingStatus.status_45',
  90: 'trackingStatus.status_90',
  50: 'trackingStatus.status_50',
  60: 'trackingStatus.status_60',
  70: 'trackingStatus.status_70',
  100: 'trackingStatus.status_100',
  110: 'trackingStatus.status_110',
  120: 'trackingStatus.status_120',
  200: 'trackingStatus.status_200',
  201: 'trackingStatus.status_201',
  202: 'trackingStatus.status_202',
  300: 'trackingStatus.status_300',
  310: 'trackingStatus.status_310',
  NO_TRACKING_AVAILABLE: 'trackingStatus.status_10',
  IN_LOCAL_WAREHOUSE: 'trackingStatus.status_40',
  IN_POSTAL_NETWORK: 'trackingStatus.status_20',
  ARRIVED_AT_DESTINATION: 'trackingStatus.status_60',
};

export const currencyFormatter = (currency?: string) =>
  new Intl.NumberFormat(
    (locales[getSafeLanguage(i18n)] ?? fallbackLocale).code,
    currency
      ? {
          style: 'currency',
          currency: currency,
        }
      : undefined,
  );

export const dateSatisfiesReturnPolicy = (
  orderDate: Date | number,
  returnPolicyDays: number,
) => {
  const endOfReturnPolicy = addDays(orderDate, returnPolicyDays);
  const orderMatchesReturnPolicy =
    isBefore(new Date(), endOfReturnPolicy) ||
    isSameDay(new Date(), endOfReturnPolicy);

  return orderMatchesReturnPolicy;
};

const fallbackLocale = enGB;

export const dateFnsLocales: Record<string, Locale> = {
  en: fallbackLocale,
  es,
  fr,
  it,
  nl,
  de,
  pl,
  fi,
  da,
  sv,
  pt,
  ro,
  hu,
  bg,
  et,
  hr,
  lt,
  lv,
};

const locales = dateFnsLocales;

export const formatDaysToReturn = (
  orderDate: Date | number,
  returnPolicyDays: number,
) => {
  const endOfReturnPolicy = addDays(orderDate, returnPolicyDays);
  const orderMatchesReturnPolicy =
    isBefore(new Date(), endOfReturnPolicy) ||
    isSameDay(new Date(), endOfReturnPolicy);

  return orderMatchesReturnPolicy
    ? formatDistanceToNowStrict(endOfReturnPolicy, {
        unit: 'day',
        locale: locales[getSafeLanguage(i18n)],
      })
    : i18n.getResource(
        getSafeLanguage(i18n),
        'translation',
        'ordersListing.returnPolicyDaysExpired',
      );
};

export const formatDate = (date: Date | number) =>
  intlFormat(
    date,
    { month: 'long', day: 'numeric', year: 'numeric' },
    {
      locale: (locales[getSafeLanguage(i18n)] ?? fallbackLocale).code,
    },
  );
